/**
 * The drivers' words, on the desk.
 *
 * One thread per driver, the office being one party however many desks it has: a driver
 * asking "which entrance?" does not care which dispatcher answers, and a colleague's answer
 * leaves this desk's list too. The card lists every thread with what is unread, and opens
 * one at a time underneath — the ready phrases first, because most of what a desk says to
 * a car is one of six things.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { driversApi, type FleetDriver } from '../settings/drivers';
import { useTenantFormats } from '../shell/formats';
import {
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  Count,
  cx,
  EmptyState,
  Select,
  Sub,
  Textarea,
} from '../ui';
import { messagesApi, OFFICE_PRESETS, type Message, type Thread } from './api';
import { useThreads } from './useThreads';
import { wordsOf } from './words';

export function MessagesCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const { threads, unread, enabled, refresh } = useThreads();

  const [open, setOpen] = useState<string | null>(null);
  const [lines, setLines] = useState<Message[]>([]);
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async (driverId: string) => {
    try {
      setLines(await messagesApi.thread(driverId));

      // Opening a thread is reading it, on every desk at once.
      await messagesApi.read(driverId);
    } catch {
      // A thread that cannot be loaded is an empty thread for a moment; the list above
      // it still says what is unread.
    }
  }, []);

  // The roster, for writing to a driver who has never written in. Fetched once; a hire
  // during a shift reaches it on the next reload, which is soon enough for a list.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const roster = await driversApi.list();

        if (!cancelled) setDrivers(roster.filter((driver) => driver.isActive));
      } catch {
        // A dispatcher may not read the roster. Threads still open from the list.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (open === null) return;

    let cancelled = false;

    void (async () => {
      try {
        const thread = await messagesApi.thread(open);

        if (cancelled) return;

        setLines(thread);
        await messagesApi.read(open);
      } catch {
        // As in load().
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // The open thread follows the hub as the list does; a word from the car appears without
  // anybody pressing anything.
  useDispatcherHub({
    onDriverMessageChanged: (change) => {
      if (change.driverId === open) void load(open);
    },
  });

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [lines]);

  async function send(draft: { code: string | null; body: string | null }) {
    if (open === null) return;

    setBusy(true);
    setError(null);

    try {
      await messagesApi.send(open, draft);
      setText('');
      await load(open);
      await refresh();
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), { defaultValue: t('messages.sendFailed') })
          : t('messages.sendFailed'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!enabled) return null;

  const opened = threads.find((thread) => thread.driverId === open);
  const openedName =
    opened?.driverName ?? drivers.find((driver) => driver.driverId === open)?.displayName ?? '';

  // Drivers with no thread yet, offered in the select so a desk can start one.
  const fresh = drivers.filter((driver) => !threads.some((thread) => thread.driverId === driver.driverId));

  return (
    <Card className={className}>
      <CardHeader
        title={
          <>
            {t('messages.title')}
            {unread > 0 && <Count className="ml-2 bg-brand text-on-brand">{unread}</Count>}
          </>
        }
        actions={
          fresh.length > 0 && (
            <Select
              aria-label={t('messages.writeTo')}
              value=""
              onChange={(event) => {
                if (event.target.value !== '') setOpen(event.target.value);
              }}
            >
              <option value="">{t('messages.writeTo')}</option>
              {fresh.map((driver) => (
                <option key={driver.driverId} value={driver.driverId}>
                  {driver.displayName}
                </option>
              ))}
            </Select>
          )
        }
      />

      {threads.length === 0 && open === null ? (
        <EmptyState>{t('messages.empty')}</EmptyState>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col">
          {threads.map((thread) => (
            <ThreadRow
              key={thread.driverId}
              thread={thread}
              current={thread.driverId === open}
              time={formats.time(thread.last.sentAt)}
              words={wordsOf(t, thread.last)}
              onOpen={() => setOpen(thread.driverId === open ? null : thread.driverId)}
            />
          ))}
        </ul>
      )}

      {open !== null && (
        <div className="border-t border-rule px-3.5 pt-2.5 pb-3 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <strong className="font-semibold truncate">{openedName}</strong>
            <Button size="sm" variant="quiet" onClick={() => setOpen(null)}>
              {t('messages.close')}
            </Button>
          </div>

          {/* The last hundred lines, the office's on the right as in any thread. */}
          <div className="max-h-64 overflow-y-auto flex flex-col gap-1">
            {lines.length === 0 && <Sub>{t('messages.nothingYet')}</Sub>}
            {lines.map((line) => (
              <div
                key={line.id}
                className={cx(
                  'max-w-[85%] px-2.5 py-1.5 rounded-control text-secondary',
                  line.direction === 'ToDriver'
                    ? 'self-end bg-brand-soft text-ink'
                    : 'self-start bg-surface-2 text-ink',
                )}
              >
                <span>{wordsOf(t, line)}</span>
                <Sub block>
                  {formats.time(line.sentAt)}
                  {line.direction === 'ToDriver' && line.readAt !== null && ` · ${t('messages.read')}`}
                </Sub>
              </div>
            ))}
            <div ref={end} />
          </div>

          {/* The ready phrases first: most of what a desk says to a car is one of these. */}
          <ChipGroup label={t('messages.presets')}>
            {OFFICE_PRESETS.map((code) => (
              <Chip key={code} onClick={() => void send({ code, body: null })}>
                {t(`driverMessages.office.${code}`)}
              </Chip>
            ))}
          </ChipGroup>

          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (text.trim().length > 0) void send({ code: null, body: text });
            }}
          >
            <Textarea
              className="flex-1"
              rows={2}
              value={text}
              maxLength={500}
              placeholder={t('messages.placeholder')}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, as in every chat; Shift+Enter is a new line.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (text.trim().length > 0) void send({ code: null, body: text });
                }
              }}
            />
            <Button variant="primary" type="submit" disabled={busy || text.trim().length === 0}>
              {t('messages.send')}
            </Button>
          </form>

          {error !== null && <span className="text-secondary text-danger">{error}</span>}
        </div>
      )}
    </Card>
  );
}

function ThreadRow({
  thread,
  current,
  time,
  words,
  onOpen,
}: {
  thread: Thread;
  current: boolean;
  time: string;
  words: string;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={current}
        onClick={onOpen}
        className={cx(
          'w-full flex items-center gap-2 px-3.5 py-2 text-left border-t border-rule',
          current ? 'bg-surface-2' : 'hover:bg-surface-2',
        )}
      >
        <span className="flex-1 min-w-0">
          <span className="flex items-baseline justify-between gap-2">
            <span className={cx('truncate', thread.unreadFromDriver > 0 && 'font-semibold')}>
              {thread.driverName}
              {thread.callSign !== null && <Sub> {thread.callSign}</Sub>}
            </span>
            <Sub>{time}</Sub>
          </span>
          <span className="block truncate text-secondary text-ink-2">
            {thread.last.direction === 'ToDriver' ? '↩ ' : ''}
            {words}
          </span>
        </span>
        {thread.unreadFromDriver > 0 && (
          <Count className="bg-brand text-on-brand">{thread.unreadFromDriver}</Count>
        )}
      </button>
    </li>
  );
}
